import React, { useState } from "react";
import moment from "moment";
import addAdminCode from "../../helpers/addAdminCode";
import checkAdminCode, { CodeCheckStatus } from "../../helpers/checkAdminCode";
import { AdminParticipantEntry } from "../../helpers/adminTypes";
import { IconBarcode, IconBarcodeOff, IconCheck } from "./AdminIcons";
import "../../Admin.scss";

const statusLabel: Record<CodeCheckStatus, string> = {
  used: "Iskorišćen",
  available: "Nije iskorišćen",
  not_found: "Ne postoji u bazi",
};

const statusHint: Record<CodeCheckStatus, string> = {
  used: "Kod je već uneo takmičar — prijava postoji.",
  available: "Kod je u bazi kodova i još nije iskorišćen.",
  not_found: "Ovaj kod nije u bazi kodova.",
};

const addMessageForCode = (code: number, addedCode?: string): string => {
  switch (code) {
    case 201:
      return `Kod ${addedCode} je dodat u bazu kodova.`;
    case 400:
      return "Kod mora imati tačno 10 slova ili cifara.";
    case 405:
      return "Kod je već iskorišćen — postoji prijava.";
    case 409:
      return "Kod već postoji u bazi kodova.";
    default:
      return "Dodavanje nije uspelo. Pokušaj ponovo.";
  }
};

const initials = (name: string | null): string => {
  if (!name) return "?";
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

const formatEntryDate = (value: string): string => {
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD.MM.YYYY HH:mm") : value;
};

const AdminCodes: React.FC = () => {
  const [checkValue, setCheckValue] = useState("");
  const [addValue, setAddValue] = useState("");
  const [checkLoading, setCheckLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [checkStatus, setCheckStatus] = useState<CodeCheckStatus | null>(null);
  const [checkEntry, setCheckEntry] = useState<AdminParticipantEntry | null>(null);
  const [checkedCode, setCheckedCode] = useState("");
  const [addMessage, setAddMessage] = useState("");
  const [addOk, setAddOk] = useState(false);
  const [addedCode, setAddedCode] = useState("");

  const handleCheck = async (event: React.FormEvent) => {
    event.preventDefault();
    setCheckLoading(true);
    setCheckStatus(null);
    setCheckEntry(null);
    const result = await checkAdminCode(checkValue);
    setCheckLoading(false);
    setCheckStatus(result.data?.status ?? null);
    setCheckEntry(result.data?.entry ?? null);
    setCheckedCode(checkValue.trim().toUpperCase());
  };

  const handleAdd = async (event: React.FormEvent) => {
    event.preventDefault();
    const normalized = addValue.trim().toUpperCase();
    if (!/^[A-Z0-9]{10}$/.test(normalized)) {
      setAddOk(false);
      setAddedCode("");
      setAddMessage("Kod mora imati tačno 10 slova ili cifara.");
      return;
    }

    setAddLoading(true);
    setAddMessage("");
    setAddedCode("");
    const result = await addAdminCode(normalized);
    setAddLoading(false);
    setAddOk(result.code === 201);
    setAddMessage(addMessageForCode(result.code, normalized));
    if (result.code === 201) {
      setAddedCode(normalized);
      setAddValue("");
    }
  };

  return (
    <section className="admin-page">
      <header className="admin-page-head">
        <div>
          <h1>Kodovi</h1>
          <p className="admin-lede">
            Provera da li je kod važeći i da li ga je takmičar već uneo · dodavanje
            novog koda u bazu kodova
          </p>
        </div>
      </header>

      <div className="admin-grid-2">
        <form className="admin-card admin-codes-panel is-check" onSubmit={handleCheck}>
          <h2>Provera koda</h2>
          <p className="admin-codes-hint">
            Unesi kod sa zatvarača i proveri da li je iskorišćen, slobodan ili ne
            postoji u bazi kodova.
          </p>
          <div className="admin-search-field">
            <span className="admin-search-label">Kod</span>
            <div className="admin-input-icon is-code">
              <IconBarcode />
              <input
                id="check-code"
                value={checkValue}
                onChange={(e) => setCheckValue(e.target.value)}
                placeholder="npr. R4NNR5M7R5"
                required
              />
            </div>
          </div>
          <button type="submit" className="admin-btn admin-search-btn" disabled={checkLoading}>
            {checkLoading ? "Provera..." : "Proveri"}
          </button>

          {checkStatus && (
            <div className={`admin-code-result is-${checkStatus}`}>
              <div className="admin-code-result-top">
                {checkStatus === "used" && checkEntry ? (
                  <div className="admin-person">
                    <span className="admin-avatar">{initials(checkEntry.name)}</span>
                    <span>
                      <strong>{checkEntry.name || "Takmičar"}</strong>
                      <small>{statusHint.used}</small>
                    </span>
                  </div>
                ) : (
                  <div className="admin-person">
                    <span className="admin-avatar is-code">
                      {checkStatus === "not_found" ? (
                        <IconBarcodeOff />
                      ) : (
                        <IconBarcode />
                      )}
                    </span>
                    <span>
                      <strong>{checkedCode || "—"}</strong>
                      <small>{statusHint[checkStatus]}</small>
                    </span>
                  </div>
                )}
                <span className={`admin-pill is-${checkStatus}`}>
                  {statusLabel[checkStatus]}
                </span>
              </div>

              {checkStatus === "used" && checkEntry && (
                <div className="admin-code-meta">
                  <div>
                    <span>Kod</span>
                    <strong>{checkEntry.code}</strong>
                  </div>
                  <div>
                    <span>Telefon</span>
                    <strong>{checkEntry.identity}</strong>
                  </div>
                  <div>
                    <span>Email</span>
                    <strong>{checkEntry.email || "—"}</strong>
                  </div>
                  <div>
                    <span>Datum</span>
                    <strong>{formatEntryDate(checkEntry.date)}</strong>
                  </div>
                  <div>
                    <span>Tip</span>
                    <strong>{checkEntry.type}</strong>
                  </div>
                </div>
              )}
            </div>
          )}
        </form>

        <form className="admin-card admin-codes-panel is-add" onSubmit={handleAdd}>
          <h2>Dodaj kod</h2>
          <p className="admin-codes-hint">
            Dodaj novi važeći kod u bazu kodova. Takmičar ga tek posle toga može da
            unese na sajtu — ovo nije prijava.
          </p>
          <div className="admin-search-field">
            <span className="admin-search-label">Novi kod</span>
            <div className="admin-input-icon is-code">
              <IconBarcode />
              <input
                id="add-code"
                value={addValue}
                onChange={(e) => setAddValue(e.target.value)}
                placeholder="npr. NOVIKOD001"
                required
                maxLength={10}
              />
            </div>
          </div>
          <button type="submit" className="admin-btn admin-search-btn" disabled={addLoading}>
            {addLoading ? "Dodavanje..." : "Dodaj u bazu kodova"}
          </button>
          {addMessage &&
            (addOk ? (
              <p className="admin-add-success">
                <IconCheck />
                <span>
                  Kod <strong>{addedCode}</strong> je dodat u bazu kodova.
                </span>
              </p>
            ) : (
              <p className="admin-error">{addMessage}</p>
            ))}
        </form>
      </div>
    </section>
  );
};

export default AdminCodes;
